import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { TileInterface } from "src/app/core/interfaces/tile-interface";
import { Tile } from "src/app/shared/components/tile/tile";
import { Loader } from "src/app/shared/components/loader/loader";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { TranslocoModule } from "@jsverse/transloco";
import { Toggle } from "src/app/shared/components/toggle/toggle";
import { UserStore } from "../../store/user-store";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteTileErrorModal } from "src/app/shared/components/favorite-tile-error-modal/favorite-tile-error-modal";
import { ErrorModalService } from "src/app/core/services/error-modal-service";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";

@Component({
  selector: "app-mobility-page",
  imports: [Tile, Toggle, Loader, TranslocoModule, FavoriteTileErrorModal],
  templateUrl: "./mobility-page.html",
})
export class MobilityPage implements OnInit {
  private readonly tilesApi = inject(TilesApiService);
  private readonly piano = inject(PianoAnalyticsService);
  private readonly discoverThemeLabel =
    "Découvrir nos métiers et nos implantations";
  private readonly discussThemeLabel = "Échanger sur mon projet de mobilité";
  private readonly applyThemeLabel =
    "Postuler sur une offre de mobilité du Groupe";

  private readonly languageService = inject(LanguageService);

  private readonly errorModalService = inject(ErrorModalService);
  
  loading = signal<boolean>(false);
  tilesDiscover: TileInterface[] = [];
  tilesDiscuss: TileInterface[] = [];
  tilesApply: TileInterface[] = [];
  tilesDiscoverToShow = signal<TileInterface[]>([]);
  tilesDiscussToShow = signal<TileInterface[]>([]);
  tilesApplyToShow = signal<TileInterface[]>([]);

  userStore = inject(UserStore);
  userRole = computed(() => this.userStore.getUserPermission());

  modeDraft = signal<boolean>(false);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.loadMobilityTiles();
    });
  }

  ngOnInit(): void {
    this.piano.trackPageDisplay("mobility", "/mobility");
  }

  private async loadMobilityTiles(): Promise<void> {
    this.loading.set(true);
    try {
      const tiles = await this.tilesApi.getTilesMobility();

      this.tilesDiscover = tiles
        .filter(
          (tile) =>
            this.normalizeTheme(tile.mobilityTheme) ===
            this.normalizeTheme(this.discoverThemeLabel)
        )
        .map((tile, idx) => this.tilesApi.mapTileToTileInterface(tile, idx));

      this.tilesDiscuss = tiles
        .filter(
          (tile) =>
            this.normalizeTheme(tile.mobilityTheme) ===
            this.normalizeTheme(this.discussThemeLabel)
        )
        .map((tile, idx) => this.tilesApi.mapTileToTileInterface(tile, idx));

      this.tilesApply = tiles
        .filter(
          (tile) =>
            this.normalizeTheme(tile.mobilityTheme) ===
            this.normalizeTheme(this.applyThemeLabel)
        )
        .map((tile, idx) => this.tilesApi.mapTileToTileInterface(tile, idx));
      this.updateTilesToShow();
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
    } finally {
      this.loading.set(false);
    }
  }

  private normalizeTheme(theme: string): string {
    return theme
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  updateTilesToShow(): void {
    this.tilesApi.updateTilesToShow(
      this.tilesDiscover,
      this.tilesDiscoverToShow,
      this.modeDraft()
    );
    this.tilesApi.updateTilesToShow(
      this.tilesDiscuss,
      this.tilesDiscussToShow,
      this.modeDraft()
    );
    this.tilesApi.updateTilesToShow(
      this.tilesApply,
      this.tilesApplyToShow,
      this.modeDraft()
    );
  }

  toggleDraftMode(): void {
    this.modeDraft.set(!this.modeDraft());
    this.updateTilesToShow();
  }
}
